import React from 'react';
import { TransformType, TransformConfig } from '../../models';
import { Input, Select, SelectOption } from '../ui';

const TRANSFORM_FUNCTIONS: { id: TransformType; name: string }[] = [
  { id: 'none', name: 'None' },
  { id: 'trim', name: 'Trim whitespace' },
  { id: 'number', name: 'Extract number' },
  { id: 'currency', name: 'Format as currency' },
  { id: 'split', name: 'Split text' },
  { id: 'join', name: 'Join array' },
  { id: 'regex', name: 'Apply regex' },
  { id: 'custom', name: 'Custom function' },
  { id: 'load', name: 'Load from sub-page' },
  { id: 'extractHTML', name: 'Extract HTML content' },
];

const transformOptions: SelectOption[] = TRANSFORM_FUNCTIONS.map((t) => ({
  value: t.id,
  label: t.name
}));

interface LocalValues {
  transformPattern: string;
  transformDelimiter: string;
  transformFunction: string;
  subSelector: string;
  subAttribute: string;
  subDefaultValue: string;
  subTransformDelimiter: string;
  subTransformPattern: string;
  subTransformFunction: string;
}

interface TransformConfigSectionProps {
  fieldId: string;
  transform?: TransformConfig;
  localValues: LocalValues;
  isEmpty: boolean;
  onLocalValueChange: (key: string, value: string) => void;
  onTransformTypeChange: (type: TransformType) => void;
  onTransformParamBlur: (param: string) => void;
  onSubTransformTypeChange: (type: TransformType) => void;
  onSubTransformParamBlur: (param: string) => void;
  onSubParamBlur: (param: 'subSelector' | 'subAttribute' | 'subDefaultValue') => void;
}

const TransformConfigSection: React.FC<TransformConfigSectionProps> = ({
  fieldId,
  transform,
  localValues,
  isEmpty,
  onLocalValueChange,
  onTransformTypeChange,
  onTransformParamBlur,
  onSubTransformTypeChange,
  onSubTransformParamBlur,
  onSubParamBlur,
}) => {
  return (
    <>
      <div>
        <Select
          label="Transform"
          value={transform?.type || 'none'}
          onChange={(e) => onTransformTypeChange(e.target.value as TransformType)}
          options={transformOptions}
          disabled={isEmpty}
          fullWidth
        />
      </div>

      {transform?.type === 'split' && (
        <div className="md:w-1/2">
          <Input
            label="Delimiter"
            value={localValues.transformDelimiter}
            onChange={(e) => onLocalValueChange('transformDelimiter', (e.target as HTMLInputElement).value)}
            onBlur={() => onTransformParamBlur('delimiter')}
            fullWidth
            disabled={isEmpty}
          />
        </div>
      )}

      {transform?.type === 'regex' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Pattern"
            value={localValues.transformPattern}
            onChange={(e) => onLocalValueChange('transformPattern', (e.target as HTMLInputElement).value)}
            onBlur={() => onTransformParamBlur('pattern')}
            fullWidth
            disabled={isEmpty}
          />
          <Input
            label="Replacement"
            value={localValues.transformDelimiter}
            onChange={(e) => onLocalValueChange('transformDelimiter', (e.target as HTMLInputElement).value)}
            onBlur={() => onTransformParamBlur('delimiter')}
            fullWidth
            disabled={isEmpty}
          />
        </div>
      )}

      {transform?.type === 'split' && (
        <div className="md:w-1/2">
          <Input
            label="Pattern (optional)"
            value={localValues.transformPattern}
            onChange={(e) => onLocalValueChange('transformPattern', (e.target as HTMLInputElement).value)}
            onBlur={() => onTransformParamBlur('pattern')}
            fullWidth
            disabled={isEmpty}
          />
        </div>
      )}

      {transform?.type === 'join' && (
        <div className="md:w-1/2">
          <Input
            label="Delimiter"
            value={localValues.transformDelimiter}
            onChange={(e) => onLocalValueChange('transformDelimiter', (e.target as HTMLInputElement).value)}
            onBlur={() => onTransformParamBlur('delimiter')}
            fullWidth
            disabled={isEmpty}
          />
        </div>
      )}

      {transform?.type === 'custom' && (
        <div>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
            value={localValues.transformFunction}
            onChange={(e) => onLocalValueChange('transformFunction', e.target.value)}
            onBlur={() => onTransformParamBlur('function')}
            rows={4}
            disabled={isEmpty}
            placeholder="Custom function"
          />
          <p className="text-xs text-gray-500 mt-1">
            Use 'value' and 'element' as function parameters
          </p>
        </div>
      )}

      {transform?.type === 'load' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Sub-selector"
              value={localValues.subSelector}
              onChange={(e) => onLocalValueChange('subSelector', (e.target as HTMLInputElement).value)}
              onBlur={() => onSubParamBlur('subSelector')}
              fullWidth
              disabled={isEmpty}
            />
            <Input
              label="Sub-attribute (optional)"
              value={localValues.subAttribute}
              onChange={(e) => onLocalValueChange('subAttribute', (e.target as HTMLInputElement).value)}
              onBlur={() => onSubParamBlur('subAttribute')}
              fullWidth
              disabled={isEmpty}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Sub-default value"
              value={localValues.subDefaultValue}
              onChange={(e) => onLocalValueChange('subDefaultValue', (e.target as HTMLInputElement).value)}
              onBlur={() => onSubParamBlur('subDefaultValue')}
              fullWidth
              disabled={isEmpty}
            />
            <Select
              label="Sub-transform"
              value={transform?.subTransform?.type || 'none'}
              onChange={(e) => onSubTransformTypeChange(e.target.value as TransformType)}
              options={transformOptions}
              disabled={isEmpty}
              fullWidth
            />
          </div>

          {transform?.subTransform?.type === 'split' && (
            <div className="md:w-1/2">
              <Input
                label="Sub delimiter"
                value={localValues.subTransformDelimiter}
                onChange={(e) => onLocalValueChange('subTransformDelimiter', (e.target as HTMLInputElement).value)}
                onBlur={() => onSubTransformParamBlur('delimiter')}
                fullWidth
                disabled={isEmpty}
              />
            </div>
          )}

          {transform?.subTransform?.type === 'regex' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Sub pattern"
                value={localValues.subTransformPattern}
                onChange={(e) => onLocalValueChange('subTransformPattern', (e.target as HTMLInputElement).value)}
                onBlur={() => onSubTransformParamBlur('pattern')}
                fullWidth
                disabled={isEmpty}
              />
              <Input
                label="Sub replacement"
                value={localValues.subTransformDelimiter}
                onChange={(e) => onLocalValueChange('subTransformDelimiter', (e.target as HTMLInputElement).value)}
                onBlur={() => onSubTransformParamBlur('delimiter')}
                fullWidth
                disabled={isEmpty}
              />
            </div>
          )}

          {transform?.subTransform?.type === 'join' && (
            <div className="md:w-1/2">
              <Input
                label="Sub delimiter"
                value={localValues.subTransformDelimiter}
                onChange={(e) => onLocalValueChange('subTransformDelimiter', (e.target as HTMLInputElement).value)}
                onBlur={() => onSubTransformParamBlur('delimiter')}
                fullWidth
                disabled={isEmpty}
              />
            </div>
          )}

          {transform?.subTransform?.type === 'custom' && (
            <div>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                value={localValues.subTransformFunction}
                onChange={(e) => onLocalValueChange('subTransformFunction', e.target.value)}
                onBlur={() => onSubTransformParamBlur('function')}
                rows={4}
                disabled={isEmpty}
                placeholder="Sub custom function"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use 'value' and 'element' as function parameters
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TransformConfigSection;
